import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Phone, Syringe, Sparkles, Droplets, GitBranch, Leaf, Scissors, Anchor, Stethoscope, UserCircle, CircleDot, ScanLine } from "lucide-react";
import SEO from "@/components/SEO";
import { WHATSAPP_URL as WHATSAPP_LINK, TEL_URL as PHONE_LINK } from "@/constants/contact";

const treatments = [
  {
    name: "Hair Fall & Hair Loss",
    description: "Diagnosis-first care for thinning, shedding, and pattern loss — medical management, PRP, and transplant planning.",
    link: "/hair-fall-hair-loss-treatment-mumbai",
    icon: Stethoscope,
  },
  { name: "Thread Lift (PDO)", description: "Non-surgical face lifting with dissolvable threads and minimal downtime.", link: "/thread-lift-mumbai", icon: Anchor },
  { name: "Botox Treatment", description: "Precise botulinum toxin for expression lines, jaw slimming, and medical uses such as hyperhidrosis.", link: "/botox-treatment-mumbai", icon: Syringe },
  {
    name: "Dermal Fillers",
    description: "Restore facial volume, contour the jawline and cheeks, and refine lips and under-eyes with hyaluronic acid fillers.",
    link: "/dermal-fillers-mumbai",
    icon: CircleDot,
  },
  {
    name: "Liquid Rhinoplasty",
    description: "Non-surgical nose reshaping with hyaluronic acid filler — profile balance, hump camouflage, and reversible results with strict nasal safety protocols.",
    link: "/liquid-rhinoplasty-mumbai",
    icon: ScanLine,
  },
  {
    name: "Chemical Peels",
    description: "Customized superficial to medium-depth peels for acne, pigmentation, melasma, and dull skin — with protocols for Indian skin.",
    link: "/chemical-peels-mumbai",
    icon: Sparkles,
  },
  { name: "PRP Therapy", description: "Platelet-rich plasma for early hair thinning, shedding, and post-transplant support.", link: "/prp-therapy-hair-loss-mumbai", icon: Droplets },
  { name: "Spider Vein Treatment", description: "Sclerotherapy to eliminate visible leg veins safely and effectively.", link: "/spider-vein-treatment-in-mumbai", icon: GitBranch },
  { name: "Hair Transplant", description: "Permanent hair restoration surgery with natural-looking results.", link: "/hair-transplant-mumbai", icon: Leaf },
  { name: "Beard Transplant", description: "Permanent FUE and DHI beard restoration for patchy, sparse, or scarred facial hair.", link: "/beard-transplant-mumbai", icon: UserCircle },
  { name: "Scalp Reduction", description: "Surgical reduction of bald areas for patients with significant hair loss.", link: "/scalp-reduction-in-mumbai", icon: Scissors },
];

const nonSurgicalStructuredData = {
  "@context": "https://schema.org",
  "@type": "MedicalWebPage",
  name: "Non-Surgical Treatments in Mumbai — Dr. Vicky Jain",
  url: "https://drvickyjain.com/non-surgical-treatments-in-mumbai",
  description: "Non-surgical aesthetic treatments in Mumbai by Dr. Vicky Jain — Botox, dermal fillers, PDO thread lift, PRP, hair transplant, and more.",
  medicalAudience: { "@type": "MedicalAudience", audienceType: "Patient" },
  about: { "@type": "MedicalSpecialty", name: "Aesthetic Medicine" },
  author: { "@type": "Physician", name: "Dr. Vicky Jain", url: "https://drvickyjain.com/about" },
};

const NonSurgicalPage = () => {
  return (
    <div className="bg-warm-white min-h-screen">
      <SEO title="Non-Surgical Treatments in Mumbai | Dr. Vicky Jain" description="Non-surgical aesthetic treatments in Mumbai by Dr. Vicky Jain — Botox, dermal fillers, PDO thread lift, PRP, hair transplant, and more. Book a consultation." canonical="/non-surgical-treatments-in-mumbai" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(nonSurgicalStructuredData) }} />
      <section className="bg-warm-white pt-32 pb-20 px-4 md:px-8">
        <div className="container mx-auto max-w-7xl">
          <p className="text-sm font-medium tracking-widest text-gold uppercase mb-4">Non-Surgical</p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-premium-purple mb-6 max-w-3xl">
            Non-Surgical Treatments
          </h1>
          <p className="text-lg text-deep-navy/70 max-w-2xl leading-relaxed">
            Achieve meaningful aesthetic improvement without surgery. Results-focused treatments administered by a board-certified plastic surgeon — not a beautician.
          </p>
        </div>
      </section>

      <section className="bg-warm-white py-16 px-4 md:px-8">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {treatments.map((treatment, index) => (
              <div key={index} className="bg-white p-8 border border-deep-navy/10">
                <treatment.icon className="w-7 h-7 text-gold mb-5" strokeWidth={1.5} />
                <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{treatment.name}</h3>
                <p className="text-deep-navy/70 leading-relaxed mb-6">{treatment.description}</p>
                <Link to={treatment.link} className="text-gold font-medium hover:text-deep-navy transition-colors">
                  Learn More →
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-premium-purple py-16 px-4 md:px-8">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="font-serif text-3xl text-warm-white mb-4">Book Your Consultation</h2>
              <p className="text-warm-white/70">Not sure which treatment is right for you? Dr. Jain will advise based on your specific concern.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild className="btn-gold border-none rounded-none px-8 py-6">
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">Book Consultation</a>
              </Button>
              <Button asChild variant="outline" className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6">
                <a href={PHONE_LINK}><Phone className="w-4 h-4 mr-2" />Call Now</a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NonSurgicalPage;
